import { BACKEND_URL } from "@/lib/backendUrl";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Paperclip, Printer } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Chip, Loading } from "@/components/common/Chrome";
import { api } from "@/lib/api";
import { dateTime, money, shortDate } from "@/lib/format";

const API_BASE = `${BACKEND_URL}/api`;

const Section = ({ title, kicker, children, testId }) => (
  <section className="break-inside-avoid space-y-2" data-testid={testId}>
    <div>
      {kicker && <div className="kicker">{kicker}</div>}
      <h2 className="text-lg font-semibold text-foreground">{title}</h2>
    </div>
    {children}
  </section>
);

export default function BoardPack() {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const [pack, setPack] = useState(null);

  const load = useCallback(async () => {
    const res = await api.get(`/charity/meetings/${meetingId}/pack`);
    setPack(res.data);
  }, [meetingId]);

  useEffect(() => { load().catch(() => setPack(false)); }, [load]);

  if (!pack) return <Loading label="Assembling the board pack" />;
  const { meeting, governance, decisions, organization } = pack;

  return (
    <div className="mx-auto max-w-4xl space-y-8 rounded-2xl border border-border bg-card p-6 shadow-sm print:max-w-none print:border-0 print:bg-white print:p-0 print:shadow-none"
         data-testid="board-pack">
      <div className="flex flex-wrap items-start justify-between gap-3 print:hidden">
        <Button size="sm" variant="outline" data-testid="pack-back-button"
                onClick={() => navigate("/charity/governance")}
                className="h-8 rounded-full border-border text-[11px]">
          <ArrowLeft className="mr-1 h-3.5 w-3.5" /> Back to governance
        </Button>
        <Button size="sm" data-testid="pack-print-button" onClick={() => window.print()}
                className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
          <Printer className="mr-1 h-3.5 w-3.5" /> Print the pack
        </Button>
      </div>

      <header className="space-y-2 border-b border-border pb-5">
        <div className="kicker">Trustee board pack · {organization.name}</div>
        <h1 className="text-3xl font-semibold text-foreground">{meeting.title}</h1>
        <div className="text-sm text-muted-foreground">
          {meeting.meeting_type} meeting · {shortDate(meeting.meeting_date)} · status {meeting.status}
        </div>
        <div className="text-[11.5px] text-muted-foreground">
          Prepared for {pack.generated_for} on {dateTime(pack.generated_at)}. Figures are as at{" "}
          {shortDate(pack.as_at)} for financial year {governance.financial_year}.
        </div>
      </header>

      <Section kicker="1" title="Attendance" testId="pack-attendance">
        {(meeting.attendees || []).length ? (
          <div className="flex flex-wrap gap-1.5">
            {meeting.attendees.map((a) => (
              <Chip key={a} className="border-border bg-muted text-foreground/80">{a}</Chip>
            ))}
          </div>
        ) : <p className="text-xs text-muted-foreground">No attendees have been recorded.</p>}
        {governance.trustees.length > 0 && (
          <p className="text-[11.5px] text-muted-foreground">
            Board as constituted: {governance.trustees.map((t) => `${t.name} (${t.role})`).join(", ")}
          </p>
        )}
      </Section>

      <Section kicker="2" title="Agenda" testId="pack-agenda">
        {(meeting.agenda || []).length ? (
          <ol className="list-inside list-decimal space-y-1 text-sm text-foreground/85">
            {meeting.agenda.map((a) => <li key={a}>{a}</li>)}
          </ol>
        ) : <p className="text-xs text-muted-foreground">No agenda was circulated.</p>}
      </Section>

      <Section kicker="3" title="Decisions and minutes" testId="pack-decisions">
        {decisions.length === 0 && (
          <p className="text-xs text-muted-foreground">
            No approval decisions are minuted against this meeting yet.
          </p>
        )}
        <div className="space-y-3">
          {decisions.map((d) => (
            <article key={d.request_ref} className="break-inside-avoid rounded-xl border border-border p-4"
                     data-testid={`pack-decision-${d.request_ref}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <span className="font-mono text-[11px] text-muted-foreground">{d.request_ref}</span>
                  <div className="text-sm font-semibold text-foreground">{d.subject}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Chip className={d.status === "approved"
                    ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                    : "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300"}>
                    {d.status}
                  </Chip>
                  <span className="text-[11.5px] text-muted-foreground">
                    {money(d.amount)} · {d.approvals} for / {d.declines} against
                  </span>
                </div>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{d.reason}</p>
              {d.minute && (
                <div className="mt-2 space-y-1 rounded-lg bg-muted/30 p-3 text-[11.5px]">
                  <div className="text-foreground">“{d.minute.resolution}”</div>
                  <div className="text-muted-foreground">Recusal: {d.minute.recusal_note}</div>
                  <div className="text-muted-foreground">
                    Minuted by {d.minute.minuted_by} · {shortDate(d.minute.meeting_date)}
                  </div>
                  {d.minute.document && (
                    <a href={`${API_BASE}/conflicts/clearances/${d.request_ref}/minute/document`}
                       target="_blank" rel="noreferrer"
                       data-testid={`pack-doc-${d.request_ref}`}
                       className="inline-flex items-center gap-1 text-primary hover:underline">
                      <Paperclip className="h-3 w-3" /> {d.minute.document.filename}
                    </a>
                  )}
                </div>
              )}
              {d.votes.length > 0 && (
                <ul className="mt-2 space-y-0.5 text-[11px]">
                  {d.votes.map((v, i) => (
                    <li key={i}>
                      <span className={v.decision === "approve"
                        ? "font-semibold text-emerald-600 dark:text-emerald-400"
                        : "font-semibold text-red-600 dark:text-red-400"}>
                        {v.decision === "approve" ? "For" : "Against"}
                      </span>{" "}
                      <span className="text-muted-foreground">
                        {(v.role || "").replace(/_/g, " ")} · {shortDate(v.at)}
                      </span>{" "}
                      <span className="text-foreground/80">“{v.minute}”</span>
                    </li>
                  ))}
                </ul>
              )}
            </article>
          ))}
        </div>
      </Section>

      <Section kicker="4" title="Matters still open" testId="pack-open">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-border p-3">
            <div className="kicker">Conflicted decisions held</div>
            {governance.open_conflicts.length === 0
              ? <p className="mt-1 text-xs text-muted-foreground">None.</p>
              : governance.open_conflicts.map((c) => (
                <div key={c.request_ref} className="mt-1 text-[11.5px]">
                  <span className="font-mono text-muted-foreground">{c.request_ref}</span> {c.subject} ·{" "}
                  {money(c.amount)} · {c.approvals}/{c.required} signed
                </div>
              ))}
          </div>
          <div className="rounded-xl border border-border p-3">
            <div className="kicker">Approvals awaiting signature</div>
            {governance.pending_approvals.length === 0
              ? <p className="mt-1 text-xs text-muted-foreground">None.</p>
              : governance.pending_approvals.map((c) => (
                <div key={c.request_ref} className="mt-1 text-[11.5px]">
                  <span className="font-mono text-muted-foreground">{c.request_ref}</span> {c.subject} ·{" "}
                  {money(c.amount)} · raised by {c.raised_by}
                </div>
              ))}
          </div>
        </div>
      </Section>

      <Section kicker="5" title="Budget lines needing the board's attention" testId="pack-budget">
        <p className="text-[11.5px] text-muted-foreground">
          Total budget {money(governance.budget_totals.budget)} · spent{" "}
          {money(governance.budget_totals.spent)} · committed{" "}
          {money(governance.budget_totals.committed)} · {governance.budget_totals.blocked} line(s)
          blocked as a cost centre.
        </p>
        {governance.budget_over_plan.length === 0 ? (
          <p className="text-xs text-muted-foreground">Every line is on plan.</p>
        ) : (
          <table className="w-full border-collapse text-[11.5px]">
            <thead className="bg-muted/40">
              <tr>
                {["Line", "Budget", "Spent", "Committed", "Projected", "Status"].map((h) => (
                  <th key={h} className="px-2 py-1.5 text-left font-medium text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {governance.budget_over_plan.map((b) => (
                <tr key={`${b.scope}-${b.code}`} data-testid={`pack-budget-${b.code}`}>
                  <td className="px-2 py-1.5">{b.code} <span className="text-muted-foreground">({b.scope})</span></td>
                  <td className="px-2 py-1.5 tabular-nums">{money(b.annual_budget, "INR", true)}</td>
                  <td className="px-2 py-1.5 tabular-nums">{money(b.spent, "INR", true)}</td>
                  <td className="px-2 py-1.5 tabular-nums">{money(b.committed, "INR", true)}</td>
                  <td className="px-2 py-1.5 tabular-nums">{b.projected_pct}%</td>
                  <td className="px-2 py-1.5">{b.blocked ? "blocked" : b.flag}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section kicker="6" title="Register of declared interests" testId="pack-declarations">
        {governance.declarations.length === 0 ? (
          <p className="text-xs text-muted-foreground">Nothing is currently declared.</p>
        ) : (
          <ul className="space-y-1 text-[11.5px] text-foreground/85">
            {governance.declarations.map((d) => (
              <li key={d.declaration_ref}>
                <span className="font-mono text-muted-foreground">{d.declaration_ref}</span>{" "}
                {d.person_name} — {String(d.interest_type).replace(/_/g, " ")} in {d.related_party}
                {d.declared_on ? ` (declared ${shortDate(d.declared_on)})` : ""}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <footer className="border-t border-border pt-4 text-[10.5px] text-muted-foreground">
        {organization.name}
        {organization.registration ? ` · ${organization.registration}` : ""} · pack generated from the
        approval, conflicts and budget registers. Votes and minutes are reproduced exactly as
        recorded in the tamper-evident audit trail.
      </footer>
    </div>
  );
}
